/**
 * @fileoverview Boolean entry filter builder (MOR-906 §7.8)
 *
 * Purpose: the shared "which entries are included" step behind the widget
 * builder and the report builder. The practitioner composes a bracketed
 * AND/OR expression by clicking chips — brackets, operators and conditions
 * all read left to right, exactly the flat token array the backend
 * evaluates (convex/timesheets/query/entryQuery.ts).
 *
 * NOT lives on the condition ("is none of") rather than as an operator,
 * because every time-entry dimension is single-valued.
 *
 * @component
 * @example
 * <EntryQueryBuilder
 *   tokens={spec.filter ?? []}
 *   onChange={(filter) => patch({ filter })}
 *   dimensions={dimensions}
 * />
 */

import { useMemo, useState } from "react";
import { Plus, X } from "lucide-react";

import {
  validateTokens,
  type QueryField,
  type QueryToken,
} from "@cvx/timesheets/query/entryQuery";

import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  ConditionValueList,
  type EntryQueryDimension,
} from "./ConditionValuePicker";

export type {
  EntryQueryDimension,
  EntryQueryOption,
} from "./ConditionValuePicker";

const CHIP =
  "inline-flex items-center gap-1 rounded-md border px-1.5 py-1 text-xs";

const LOGIC_PALETTE: ReadonlyArray<{ token: QueryToken; label: string }> = [
  { token: { t: "open" }, label: "(" },
  { token: { t: "close" }, label: ")" },
  { token: { t: "and" }, label: "AND" },
  { token: { t: "or" }, label: "OR" },
];

/** An operand is anything an operator may follow. */
function endsWithOperand(tokens: readonly QueryToken[]): boolean {
  const last = tokens[tokens.length - 1];
  return !!last && (last.t === "cond" || last.t === "close");
}

export function EntryQueryBuilder({
  tokens,
  onChange,
  dimensions,
}: {
  tokens: QueryToken[];
  onChange: (tokens: QueryToken[]) => void;
  dimensions: ReadonlyArray<EntryQueryDimension>;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  /** Index being edited, or null when adding a new condition. */
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [field, setField] = useState<QueryField | null>(null);
  const [values, setValues] = useState<string[]>([]);

  const error = useMemo(() => validateTokens(tokens), [tokens]);
  const dimensionFor = (f: QueryField) => dimensions.find((d) => d.field === f);

  const openForEdit = (index: number) => {
    const token = tokens[index];
    if (token?.t !== "cond") return;
    setEditIndex(index);
    setField(token.field);
    setValues(token.values);
    setPickerOpen(true);
  };

  const apply = (negate: boolean) => {
    if (!field || values.length === 0) return;
    const condition: QueryToken = { t: "cond", field, negate, values };
    if (editIndex !== null) {
      const next = [...tokens];
      next[editIndex] = condition;
      onChange(next);
    } else {
      // Imply AND when a condition follows an operand — the common case,
      // and it keeps the expression valid as it is built.
      onChange(
        endsWithOperand(tokens)
          ? [...tokens, { t: "and" }, condition]
          : [...tokens, condition],
      );
    }
    setPickerOpen(false);
  };

  const appendToken = (token: QueryToken) => {
    if (token.t === "open" && endsWithOperand(tokens)) {
      onChange([...tokens, { t: "and" }, token]);
      return;
    }
    onChange([...tokens, token]);
  };

  /**
   * Remove a token, taking an orphaned operator with it — deleting a
   * condition should not leave a dangling AND for the user to clean up.
   */
  const removeAt = (index: number) => {
    const next = [...tokens];
    const token = next[index];
    if (token.t === "cond" || token.t === "open" || token.t === "close") {
      const before = next[index - 1];
      const after = next[index + 1];
      if (before && (before.t === "and" || before.t === "or")) {
        next.splice(index - 1, 2);
        onChange(next);
        return;
      }
      if (after && (after.t === "and" || after.t === "or")) {
        next.splice(index, 2);
        onChange(next);
        return;
      }
    }
    next.splice(index, 1);
    onChange(next);
  };

  const toggleOperator = (index: number) => {
    const token = tokens[index];
    if (token.t !== "and" && token.t !== "or") return;
    const next = [...tokens];
    next[index] = { t: token.t === "and" ? "or" : "and" };
    onChange(next);
  };

  const labelForValues = (token: QueryToken & { t: "cond" }) => {
    const dimension = dimensionFor(token.field);
    const labels = token.values.map(
      (value) =>
        dimension?.options.find((o) => o.value === value)?.label ?? value,
    );
    if (labels.length <= 2) return labels.join(", ");
    return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
  };

  const activeDimension = field ? dimensionFor(field) : undefined;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">
          Include entries where
        </span>

        {tokens.length === 0 && (
          <span className="text-xs italic text-muted-foreground">
            everything in the period
          </span>
        )}

        {tokens.map((token, index) => {
          if (token.t === "open" || token.t === "close") {
            return (
              <button
                key={index}
                type="button"
                onClick={() => removeAt(index)}
                className={cn(
                  CHIP,
                  "border-border/60 bg-muted px-2 font-mono text-sm font-semibold text-muted-foreground hover:border-destructive/60 hover:text-destructive",
                )}
                title="Remove this bracket"
              >
                {token.t === "open" ? "(" : ")"}
              </button>
            );
          }
          if (token.t === "and" || token.t === "or") {
            return (
              <button
                key={index}
                type="button"
                onClick={() => toggleOperator(index)}
                className={cn(
                  CHIP,
                  "border-primary/30 bg-primary/10 px-2 font-semibold uppercase text-primary hover:bg-primary/20",
                )}
                title="Switch between AND and OR"
              >
                {token.t}
              </button>
            );
          }
          return (
            <span
              key={index}
              className={cn(CHIP, "border-border/60 bg-background pr-0.5")}
            >
              <button
                type="button"
                className="flex items-center gap-1 hover:text-primary"
                onClick={() => openForEdit(index)}
                title="Edit this condition"
              >
                <span className="font-medium">
                  {dimensionFor(token.field)?.label ?? token.field}
                </span>
                <span className="text-muted-foreground">
                  {token.negate ? "is none of" : "is any of"}
                </span>
                <span>{labelForValues(token)}</span>
              </button>
              <button
                type="button"
                className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                onClick={() => removeAt(index)}
                aria-label="Remove condition"
              >
                <X className="size-3" />
              </button>
            </span>
          );
        })}

        <Popover
          open={pickerOpen}
          onOpenChange={(open) => {
            setPickerOpen(open);
            // Opening from the trigger always starts a new condition;
            // `openForEdit` sets state itself and never routes through here.
            if (open) {
              setField(dimensions[0]?.field ?? null);
              setValues([]);
            } else {
              setEditIndex(null);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1 text-xs"
            >
              <Plus className="size-3" />
              Condition
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] p-0" align="start">
            <div className="flex flex-wrap gap-1 border-b border-border/60 p-2">
              {dimensions.map((d) => (
                <button
                  key={d.field}
                  type="button"
                  onClick={() => {
                    setField(d.field);
                    setValues([]);
                  }}
                  className={cn(
                    "rounded-md border px-2 py-1 text-xs",
                    field === d.field
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border/60 text-muted-foreground hover:text-foreground",
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <ConditionValueList
              dimension={activeDimension}
              values={values}
              onValuesChange={setValues}
              onApply={apply}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Logic</span>
        {LOGIC_PALETTE.map(({ token, label }) => (
          <button
            key={label}
            type="button"
            onClick={() => appendToken(token)}
            className="rounded-md border border-border/60 px-2 py-0.5 text-xs text-muted-foreground hover:border-primary/40 hover:text-primary"
          >
            {label}
          </button>
        ))}
        {tokens.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="ml-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            Clear filter
          </button>
        )}
        {error && (
          <span className="ml-auto text-xs text-destructive">{error}</span>
        )}
      </div>
    </div>
  );
}
